import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { COLORS, SCROLLBAR_STYLES, findKey, formatDate } from './utils/rtmUtils';
import AnalyticsKPICard from './components/AnalyticsKPICard';
import AnalyticsFilterBar from './components/AnalyticsFilterBar';
import AnalyticsChart from './components/AnalyticsChart';

const DefectAnalyticsDashboard = ({ data }) => {
  const [filters, setFilters] = useState({
    monthYear: ['All'],
    priority: ['All'],
    status: ['All'],
    classification: ['All'],
    rca: ['All'],
  });

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const sample = data[0];
    const keys = {
      id: findKey(sample, 'Key') || findKey(sample, 'ID') || 'Key',
      summary: findKey(sample, 'Summary') || 'Summary',
      status: findKey(sample, 'Status') || 'Status',
      priority: findKey(sample, 'Priority') || findKey(sample, 'Severity') || 'Priority',
      classification: findKey(sample, 'Classification') || findKey(sample, 'Type') || 'Classification',
      rca: findKey(sample, 'RCA') || findKey(sample, 'Root Cause') || 'RCA',
      created: findKey(sample, 'Created') || findKey(sample, 'Date') || 'Created',
    };

    return data.map((item, idx) => {
      const rawDate = item[keys.created];
      let d = new Date(rawDate);
      
      return {
        _id: item[keys.id] || `DEF-${idx}`,
        _summary: item[keys.summary] || '',
        _status: (item[keys.status] || 'Open').toUpperCase(),
        _priority: (item[keys.priority] || 'Medium').toUpperCase(),
        _classification: item[keys.classification] || 'Unclassified',
        _rca: item[keys.rca] || 'Not Analyzed',
        _created: isNaN(d.getTime()) ? null : d,
        _monthYear: isNaN(d.getTime()) ? 'Unknown' : d.toLocaleString('default', { month: 'short', year: 'numeric' }),
      };
    });
  }, [data]);

  const filterOptions = useMemo(() => ({
    monthYear: ['All', ...new Set(processedData.map(d => d._monthYear))].filter(v => v !== 'Unknown'),
    priority: ['All', ...new Set(processedData.map(d => d._priority))],
    status: ['All', ...new Set(processedData.map(d => d._status))],
    classification: ['All', ...new Set(processedData.map(d => d._classification))],
    rca: ['All', ...new Set(processedData.map(d => d._rca))],
  }), [processedData]);

  const filteredData = useMemo(() => processedData.filter(d => 
    (filters.monthYear.includes('All') || filters.monthYear.includes(d._monthYear)) &&
    (filters.priority.includes('All') || filters.priority.includes(d._priority)) &&
    (filters.status.includes('All') || filters.status.includes(d._status)) &&
    (filters.classification.includes('All') || filters.classification.includes(d._classification)) &&
    (filters.rca.includes('All') || filters.rca.includes(d._rca))
  ), [processedData, filters]);

  const stats = useMemo(() => {
    const total = filteredData.length;
    const critical = filteredData.filter(d => d._priority.includes('CRIT') || d._priority.includes('HIGH') || d._priority.includes('P1')).length;
    const open = filteredData.filter(d => !d._status.includes('CLOSE') && !d._status.includes('DONE')).length;
    return { total, critical, open, resolved: total - open };
  }, [filteredData]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // --- Chart Data Memoization ---
  
  const trendChartData = useMemo(() => {
    const trendMap = filteredData.reduce((acc, d) => {
      if (d._monthYear === 'Unknown') return acc;
      acc[d._monthYear] = (acc[d._monthYear] || 0) + 1;
      return acc;
    }, {});

    const labels = Object.keys(trendMap).sort((a, b) => new Date(a) - new Date(b));
    return {
      labels,
      datasets: [{
        label: 'Defect Count',
        data: labels.map(l => trendMap[l]),
        borderColor: COLORS.PRIMARY,
        backgroundColor: `${COLORS.PRIMARY}15`,
        fill: true,
        lineTension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: COLORS.PRIMARY,
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }]
    };
  }, [filteredData]);

  const severityChartData = useMemo(() => {
    const counts = filteredData.reduce((acc, d) => { acc[d._priority] = (acc[d._priority] || 0) + 1; return acc; }, {});
    return {
      labels: Object.keys(counts),
      datasets: [{
        label: 'Defects',
        data: Object.values(counts),
        backgroundColor: COLORS.CRITICAL
      }]
    };
  }, [filteredData]);

  const statusChartData = useMemo(() => {
    const counts = filteredData.reduce((acc, d) => { acc[d._status] = (acc[d._status] || 0) + 1; return acc; }, {});
    return {
      labels: Object.keys(counts),
      datasets: [{
        data: Object.values(counts),
        backgroundColor: COLORS.PALETTE
      }]
    };
  }, [filteredData]);

  const classificationChartData = useMemo(() => {
    const counts = filteredData.reduce((acc, d) => { acc[d._classification] = (acc[d._classification] || 0) + 1; return acc; }, {});
    return {
      labels: Object.keys(counts),
      datasets: [{
        label: 'Defects',
        data: Object.values(counts),
        backgroundColor: COLORS.PRIMARY
      }]
    };
  }, [filteredData]);

  const rcaChartData = useMemo(() => {
    const counts = filteredData.reduce((acc, d) => { acc[d._rca] = (acc[d._rca] || 0) + 1; return acc; }, {});
    return {
      labels: Object.keys(counts),
      datasets: [{
        data: Object.values(counts),
        backgroundColor: COLORS.PALETTE.slice(3).concat(COLORS.PALETTE.slice(0, 3))
      }]
    };
  }, [filteredData]);

  return (
    <Box sx={{ p: 1, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: "white", boxShadow: "0 10px 30px 0 rgba(0,0,0,0.05)" }}>
        <AnalyticsFilterBar 
          filters={filters} 
          options={filterOptions} 
          onFilterChange={handleFilterChange} 
        />
      </Paper>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={3}>
          <AnalyticsKPICard title="Total Defects" value={stats.total} icon={<BugReportIcon />} color={COLORS.PRIMARY} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <AnalyticsKPICard title="Critical/High" value={stats.critical} icon={<ErrorOutlineIcon />} color={COLORS.CRITICAL} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <AnalyticsKPICard title="Open" value={stats.open} icon={<TrendingUpIcon />} color={COLORS.MEDIUM} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <AnalyticsKPICard title="Resolved" value={stats.resolved} icon={<CheckCircleOutlineIcon />} color={COLORS.DONE} />
        </Grid>
      </Grid>
      
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 4, height: 400, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: COLORS.PRIMARY }}>Defect Distribution Trend 📈</Typography>
            <Box sx={{ overflowX: "auto", width: "100%", ...SCROLLBAR_STYLES }}>
              <Box sx={{ height: 300, minWidth: 600 }}>
                <AnalyticsChart type="line" data={trendChartData} options={{ scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] } }} height={300} />
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 4, height: 350, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: COLORS.PRIMARY }}>Priority Distribution</Typography>
            <AnalyticsChart type="bar" data={severityChartData} options={{ scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] } }} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 4, height: 350, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: COLORS.PRIMARY }}>Status Breakdown</Typography>
            <AnalyticsChart type="doughnut" data={statusChartData} options={{ cutoutPercentage: 70, legend: { position: 'right' } }} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 4, height: 350, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: COLORS.PRIMARY }}>Defect Classification</Typography>
            <AnalyticsChart type="horizontalBar" data={classificationChartData} options={{ scales: { xAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] } }} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 4, height: 350, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: COLORS.PRIMARY }}>Root Cause Analysis (RCA)</Typography>
            <AnalyticsChart type="pie" data={rcaChartData} options={{ legend: { position: 'right' } }} />
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: COLORS.PRIMARY }}>Defect Details Table</Typography>
        <TableContainer sx={{ maxHeight: 450, overflowY: 'auto', ...SCROLLBAR_STYLES }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8f9fa' }}>Key</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8f9fa' }}>Summary</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8f9fa' }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8f9fa' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8f9fa' }}>Classification</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8f9fa' }}>RCA Category</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8f9fa' }}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row, i) => (
                <TableRow key={i} hover>
                  <TableCell><Chip label={row._id} size="small" variant="outlined" sx={{ fontWeight: 700, color: COLORS.PRIMARY }} /></TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={row._summary}><span>{row._summary}</span></Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row._priority} 
                      size="small" 
                      sx={{ 
                        bgcolor: `${(row._priority.includes('CRIT') || row._priority.includes('HIGH')) ? COLORS.CRITICAL : COLORS.MEDIUM}20`, 
                        color: (row._priority.includes('CRIT') || row._priority.includes('HIGH')) ? COLORS.CRITICAL : COLORS.MEDIUM, 
                        fontWeight: 700, 
                        fontSize: '0.7rem' 
                      }} 
                    />
                  </TableCell>
                  <TableCell><Chip label={row._status} size="small" variant="outlined" sx={{ fontWeight: 700 }} /></TableCell>
                  <TableCell><Typography variant="body2">{row._classification}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{row._rca}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{formatDate(row._created)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DefectAnalyticsDashboard;
